import React, { useEffect, useMemo, useState } from 'react';
import {
  useColorModeValue } from "./ui/color-mode";
import {
  AspectRatio,
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Image as ChakraImage,
  Link,
  SimpleGrid,
  Spinner,
  Stat,
  Tag,
  Text,
  VStack,
  Wrap,
  WrapItem,
  useDisclosure,
  Dialog,
  Portal,
} from '@chakra-ui/react';
import {
  FaCheckCircle,
  FaChevronRight,
  FaDownload,
  FaHistory,
  FaLayerGroup,
  FaMusic,
  FaPlus,
  FaYoutube,
} from 'react-icons/fa';
import { Link as RouterLink } from 'react-router-dom';
import { extractYouTubeVideoId } from '../utils/youtubeUrl';
import { pageImageSrc, resolveUploadsUrl } from '../utils/staticUrl';
import { bandasAPI } from '../services/api';

function formatInstrumentoEntry(iv, nombreVozPorEtiqueta) {
  const nombre = iv.nombre || iv.slug || 'Instrumento';
  const voces = Array.isArray(iv.voces) ? iv.voces : [];
  if (!voces.length) return nombre;
  const labels = voces.map((v) => nombreVozPorEtiqueta(v) || v);
  return `${nombre} (${labels.join(', ')})`;
}

function instrumentosPaginaLabel(p, nombreVozPorEtiqueta) {
  const iv = Array.isArray(p.instrumentosVoces) ? p.instrumentosVoces : [];
  if (!iv.length) return null;
  return iv.map((entry) => formatInstrumentoEntry(entry, nombreVozPorEtiqueta)).join(' · ');
}

function formatFechaEs(fecha) {
  if (!fecha) return 'Sin fecha';
  const d = new Date(`${fecha}T12:00:00`);
  if (Number.isNaN(d.getTime())) return String(fecha);
  return d.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatHorarioActuacion(item) {
  const hi = item?.horaInicio != null ? String(item.horaInicio).trim() : '';
  const hf = item?.horaFin != null ? String(item.horaFin).trim() : '';
  if (hi && hf) return `${hi}–${hf}`;
  if (hi) return hi;
  return '';
}

export default function BandaRepertorioObraFicha({
  detail,
  resumenPaginasEtiquetado,
  paginasOrdenadas,
  nombreVozPorEtiqueta,
  onOpenEtiquetado,
  onViewPage,
  onDownloadPage,
  descargandoPaginaId,
  onDownloadObraCompleta,
  descargandoObraCompleta,
  onAnadirPaginas,
  onPasarAListo,
  saving,
}) {
  const panelBg = useColorModeValue('purple.50', 'rgba(88, 28, 135, 0.18)');
  const muted = useColorModeValue('gray.600', 'gray.400');
  const cardBorder = useColorModeValue('gray.200', 'gray.600');

  const youtubeId = extractYouTubeVideoId(detail?.youtubeUrl);

  const [programaciones, setProgramaciones] = useState({ total: 0, items: [] });
  const [progLoading, setProgLoading] = useState(false);
  const historicoDisc = useDisclosure();

  useEffect(() => {
    const obraId = detail?.id;
    if (!obraId) {
      setProgramaciones({ total: 0, items: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setProgLoading(true);
        const res = await bandasAPI.listObraPartituraProgramaciones(obraId);
        if (!cancelled) {
          setProgramaciones({
            total: res.data?.total ?? 0,
            items: Array.isArray(res.data?.items) ? res.data.items : [],
          });
        }
      } catch {
        if (!cancelled) setProgramaciones({ total: 0, items: [] });
      } finally {
        if (!cancelled) setProgLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [detail?.id]);

  useEffect(() => {
    historicoDisc.onClose();
    // Cerrar al cambiar de obra; onClose es estable en la práctica del disclosure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [detail?.id]);

  const resumenInstrumentos = useMemo(() => {
    const map = new Map();
    for (const p of detail?.paginas || []) {
      for (const iv of p.instrumentosVoces || []) {
        const key = iv.slug || iv.nombre || JSON.stringify(iv);
        if (!map.has(key)) {
          map.set(key, {
            slug: iv.slug,
            nombre: iv.nombre || iv.slug,
            voces: new Set(),
          });
        }
        const row = map.get(key);
        for (const v of iv.voces || []) row.voces.add(v);
      }
    }
    return [...map.values()]
      .map((x) => ({ ...x, voces: [...x.voces] }))
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }));
  }, [detail?.paginas]);

  const notasObra = (detail?.notasDirector || '').trim();
  const carpetas = (detail?.carpetasObra || []).map((co) => co.carpeta).filter(Boolean);
  const progItems = programaciones.items || [];

  return (
    <>
      <VStack align="stretch" gap={6}>
        {detail.autor ? (
          <Text fontSize="md" color={muted}>
            {detail.autor}
          </Text>
        ) : null}

        <Wrap gap={2}>
          <Badge
            colorPalette={detail.estado === 'LISTO' ? 'green' : 'gray'}
            fontSize="0.75rem"
            px={2}
            py={0.5}
            rounded="md"
          >
            {detail.estado === 'LISTO' ? 'Listo' : 'Borrador'}
          </Badge>
          {detail.genero?.nombre ? (
            <Badge colorPalette="purple" variant="outline" fontSize="0.75rem" px={2} py={0.5} rounded="md">
              {detail.genero.nombre}
            </Badge>
          ) : null}
          {carpetas.map((c) => (
            <Badge key={c.id} colorPalette="cyan" variant="subtle" fontSize="0.75rem" px={2} py={0.5} rounded="md">
              {c.nombre}
            </Badge>
          ))}
        </Wrap>

        <SimpleGrid columns={{ base: 1, sm: 3 }} gap={3}>
          <Stat.Root
            px={3}
            py={3}
            rounded="xl"
            borderWidth="1px"
            borderColor={cardBorder}
            bg={panelBg}
          >
            <Stat.Label fontSize="xs">Páginas</Stat.Label>
            <Stat.ValueText fontSize="2xl">{resumenPaginasEtiquetado.total}</Stat.ValueText>
          </Stat.Root>
          <Stat.Root
            px={3}
            py={3}
            rounded="xl"
            borderWidth="1px"
            borderColor={cardBorder}
            bg={panelBg}
          >
            <Stat.Label fontSize="xs">Etiquetadas</Stat.Label>
            <Stat.ValueText fontSize="2xl">{resumenPaginasEtiquetado.etiquetadas}</Stat.ValueText>
            <Stat.HelpText mb={0} fontSize="xs">
              con instrumento o notas
            </Stat.HelpText>
          </Stat.Root>
          <Stat.Root
            px={3}
            py={3}
            rounded="xl"
            borderWidth="1px"
            borderColor={cardBorder}
            bg={panelBg}
          >
            <Stat.Label fontSize="xs">
              <HStack gap={1}>
                <Icon boxSize={3} asChild><FaMusic /></Icon>
                <Text as="span">Programaciones</Text>
                {progLoading ? <Spinner size="xs" color="purple.400" /> : null}
              </HStack>
            </Stat.Label>
            <Stat.ValueText fontSize="2xl">{progLoading ? '…' : programaciones.total}</Stat.ValueText>
            <Stat.HelpText mb={2} fontSize="xs">
              actuaciones finalizadas
            </Stat.HelpText>
            <Button
              size="sm"
              w="full"
              colorPalette="purple"
              variant="outline"
              onClick={historicoDisc.onOpen}
              disabled={progLoading || programaciones.total === 0}><FaHistory />Ver histórico
                          </Button>
          </Stat.Root>
        </SimpleGrid>

        <HStack gap={3} flexWrap="wrap">
          <Button
            variant="outline"
            colorPalette="purple"
            size="sm"
            onClick={onOpenEtiquetado}
            disabled={resumenPaginasEtiquetado.total === 0}><FaLayerGroup />Páginas y etiquetado
                      <FaChevronRight /></Button>
          {detail.estado !== 'LISTO' ? (
            <Button colorPalette="green" size="sm" onClick={onPasarAListo} loading={saving}><FaCheckCircle />Pasar a listo
                          </Button>
          ) : null}
        </HStack>

        {resumenInstrumentos.length > 0 ? (
          <Box>
            <Text fontWeight="700" fontSize="sm" mb={2}>
              Instrumentos y voces
            </Text>
            <Wrap gap={2}>
              {resumenInstrumentos.map((iv) => (
                <WrapItem key={iv.slug || iv.nombre}>
                  <Tag.Root size="md" colorPalette="purple" variant="subtle" rounded="full">
                    {formatInstrumentoEntry(iv, nombreVozPorEtiqueta)}
                  </Tag.Root>
                </WrapItem>
              ))}
            </Wrap>
          </Box>
        ) : (
          <Text fontSize="sm" color={muted}>
            Aún no hay instrumentos etiquetados en las páginas.
          </Text>
        )}

        {notasObra ? (
          <Box borderWidth="1px" borderColor={cardBorder} rounded="xl" p={4}>
            <Text fontWeight="700" fontSize="sm" mb={2}>
              Notas del director
            </Text>
            <Text fontSize="sm" color={muted} whiteSpace="pre-wrap">
              {notasObra}
            </Text>
          </Box>
        ) : null}

        {detail.youtubeUrl ? (
          <Box borderWidth="1px" borderColor={cardBorder} rounded="xl" p={4}>
            <HStack gap={2} mb={3}>
              <Icon color="red.500" asChild><FaYoutube /></Icon>
              <Text fontWeight="700" fontSize="sm">
                Vídeo de la obra
              </Text>
            </HStack>
            {youtubeId ? (
              <AspectRatio ratio={16 / 9} maxW="560px" rounded="lg" overflow="hidden">
                <Box title="Vídeo de YouTube" border="0" asChild><iframe
                    src={`https://www.youtube.com/embed/${youtubeId}`}
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen /></Box>
              </AspectRatio>
            ) : (
              <Link
                href={detail.youtubeUrl}
                color="purple.500"
                fontSize="sm"
                target='_blank'
                rel='noopener noreferrer'>
                {detail.youtubeUrl}
              </Link>
            )}
          </Box>
        ) : null}

        {paginasOrdenadas.length > 0 ? (
          <Box>
            <Flex
              align={{ base: 'stretch', sm: 'center' }}
              justify="space-between"
              gap={3}
              mb={3}
              flexWrap="wrap"
              direction={{ base: 'column', sm: 'row' }}
            >
              <Text fontWeight="700" fontSize="sm">
                Páginas ({paginasOrdenadas.length})
              </Text>
              <HStack gap={2} flexWrap="wrap" alignSelf={{ base: 'stretch', sm: 'auto' }} justify={{ base: 'stretch', sm: 'flex-end' }}>
                <Button
                  size="sm"
                  colorPalette="purple"
                  variant="solid"
                  onClick={onAnadirPaginas}
                  flex={{ base: 1, sm: 'initial' }}
                ><FaPlus />Añadir páginas
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  colorPalette="purple"
                  onClick={onDownloadObraCompleta}
                  loading={descargandoObraCompleta}
                  flex={{ base: 1, sm: 'initial' }}
                ><FaDownload />Descargar obra completa
                </Button>
              </HStack>
            </Flex>
            <SimpleGrid columns={{ base: 3, sm: 4, md: 5, lg: 6 }} gap={3}>
              {paginasOrdenadas.map((p, idx) => {
                const iv = Array.isArray(p.instrumentosVoces) ? p.instrumentosVoces : [];
                const etiquetada = iv.length > 0 || (p.notasDirector || '').trim();
                const etiquetaInstrumentos = instrumentosPaginaLabel(p, nombreVozPorEtiqueta);
                return (
                  <Box
                    key={p.id}
                    rounded="lg"
                    overflow="hidden"
                    borderWidth="2px"
                    borderColor={etiquetada ? 'purple.300' : cardBorder}
                    cursor="pointer"
                    transition="all 0.15s ease"
                    _hover={{ borderColor: 'purple.400', shadow: 'md', transform: 'translateY(-2px)' }}
                    onClick={() => onViewPage(p.id)}
                    title={etiquetaInstrumentos || `Página ${idx + 1}`}
                  >
                    <Box px={1.5} pt={1.5} pb={1} minH="2.4rem">
                      <HStack align="flex-start" gap={1}>
                        <Box flex={1} minW={0}>
                          {etiquetaInstrumentos ? (
                            <Text
                              fontSize="xs"
                              lineHeight="short"
                              fontWeight="600"
                              color="purple.700"
                              _dark={{ color: 'purple.200' }}
                              lineClamp={2}
                            >
                              {etiquetaInstrumentos}
                            </Text>
                          ) : (
                            <Text fontSize="xs" color={muted} lineClamp={2}>
                              Sin etiquetar
                            </Text>
                          )}
                        </Box>
                        <IconButton
                          size="sm"
                          variant="ghost"
                          colorPalette="purple"
                          aria-label={`Descargar página ${idx + 1}`}
                          flexShrink={0}
                          onClick={(e) => {
                            e.stopPropagation();
                            onDownloadPage(p);
                          }}
                          loading={descargandoPaginaId === p.id}><FaDownload /></IconButton>
                      </HStack>
                    </Box>
                    <AspectRatio ratio={3 / 4}>
                      <ChakraImage
                        src={pageImageSrc(p)}
                        alt={`Página ${idx + 1}`}
                        objectFit="cover"
                        transform={`rotate(${p.rotacion || 0}deg)`}
                        transformOrigin="center"
                      />
                    </AspectRatio>
                    <Text fontSize="xs" textAlign="center" py={1} color={muted} fontWeight="600">
                      {idx + 1}
                    </Text>
                  </Box>
                );
              })}
            </SimpleGrid>
          </Box>
        ) : null}
      </VStack>

      <Dialog.Root
        open={historicoDisc.open}
        size='lg'
        placement='center'
        scrollBehavior="inside"
        onOpenChange={e => {
          if (!e.open) {
            historicoDisc.onClose();
          }
        }}
      >
        <Portal>

          <Dialog.Backdrop />
          <Dialog.Positioner>
            <Dialog.Content maxH="85vh">
              <Dialog.Header>
                <HStack gap={2}>
                  <Icon color="purple.500" asChild><FaHistory /></Icon>
                  <Text>Histórico de programaciones</Text>
                </HStack>
                <Text fontSize="sm" fontWeight="normal" color={muted} mt={1}>
                  {detail?.titulo || 'Obra'} · {programaciones.total}{' '}
                  {programaciones.total === 1 ? 'actuación finalizada' : 'actuaciones finalizadas'}
                </Text>
              </Dialog.Header>
              <Dialog.CloseTrigger />
              <Dialog.Body pb={6}>
                {progItems.length === 0 ? (
                  <Text fontSize="sm" color={muted}>
                    Aún no se ha programado en ninguna actuación finalizada.
                  </Text>
                ) : (
                  <VStack align="stretch" gap={2} maxH="60vh" overflowY="auto" pr={1}>
                    {progItems.map((item) => {
                      const cartelSrc = item.cartelUrl ? resolveUploadsUrl(item.cartelUrl) : '';
                      const horario = formatHorarioActuacion(item);
                      return (
                        <Flex
                          key={`${item.tipo}-${item.id}`}
                          align="center"
                          gap={3}
                          p={2}
                          rounded="lg"
                          borderWidth="1px"
                          borderColor={cardBorder}
                          bg={panelBg}
                        >
                          <Box
                            w="48px"
                            h="64px"
                            flexShrink={0}
                            rounded="md"
                            overflow="hidden"
                            bg="gray.200"
                            _dark={{ bg: 'gray.700' }}
                          >
                            {cartelSrc ? (
                              <ChakraImage
                                src={cartelSrc}
                                alt=""
                                w="full"
                                h="full"
                                objectFit="cover"
                              />
                            ) : (
                              <Flex w="full" h="full" align="center" justify="center">
                                <Icon color="purple.300" boxSize={4} asChild><FaMusic /></Icon>
                              </Flex>
                            )}
                          </Box>
                          <Box minW={0} flex={1}>
                            <Text lineClamp={1} fontWeight="700" fontSize="sm">
                              {item.nombre || 'Actuación'}
                            </Text>
                            <Text fontSize="xs" color={muted}>
                              {formatFechaEs(item.fecha)}
                              {horario ? ` · ${horario}` : ''}
                            </Text>
                          </Box>
                          <Link fontSize="xs" color="purple.500" flexShrink={0} asChild><RouterLink to="/banda/actuaciones" onClick={historicoDisc.onClose}>Ver
                                                      </RouterLink></Link>
                        </Flex>
                      );
                    })}
                  </VStack>
                )}
              </Dialog.Body>
            </Dialog.Content>
          </Dialog.Positioner>

        </Portal>
      </Dialog.Root>
    </>
  );
}
